import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from 'styled-components';

type Props = {
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  label?: string;
  svgIcon?: React.ReactNode;
  src?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  bgColor?: string;
  borderColor?: string;
  textColor?: string;
  alt?: string;
  borderRadius?: string;
  loading?: boolean;
  hoverBgColor?: string;
  hoverBorderColor?: string;
  hoverTextColor?: string;
  height?: string;
  hoverSvgColor?: string;
  fontWeight?: string;
};

export const IconButton = ({
  className,
  wrapperClassName,
  disabled,
  label,
  svgIcon,
  src,
  onClick,
  bgColor,
  borderColor,
  textColor,
  alt,
  borderRadius,
  loading,
  hoverBgColor,
  hoverBorderColor,
  hoverTextColor,
  height,
  hoverSvgColor,
  fontWeight,
}: Props) => {
  const theme = useTheme();
  const [isButtonHovered, setIsButtonHovered] = useState(false); // Hover state for the button
  const defaultButtonBgColor = theme.white;
  return (
    <Wrapper className={`custom-btn-wrapper ${wrapperClassName}`}>
      <StyledButton
        bgColor={bgColor || defaultButtonBgColor}
        textColor={textColor || theme.white}
        borderColor={borderColor}
        borderRadius={borderRadius || 'unset'}
        hoverBgColor={hoverBgColor}
        hoverBorderColor={hoverBorderColor}
        hoverTextColor={hoverTextColor}
        height={height}
        className={className}
        onClick={onClick}
        disabled={disabled}
        fontWeight={fontWeight || '600'}
        onMouseEnter={() => !disabled && setIsButtonHovered(true)} // Set hover state on enter if not disabled
        onMouseLeave={() => setIsButtonHovered(false)} // Reset hover state on leave
      >
        <SvgLabelWrapper>
          {(svgIcon || src) && (
            <IconWrapper
              hoverSvgColor={hoverSvgColor}
              isHovered={isButtonHovered && !disabled} // Apply hover effect only if not disabled
            >
              {svgIcon ? svgIcon : <img src={src} alt={alt || 'icon'} />}
            </IconWrapper>
          )}
          {loading ? <span className="loader"></span> : label ?? 'button'}
        </SvgLabelWrapper>
      </StyledButton>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .loader {
    width: 25px;
    height: 25px;
    border: 3.5px solid ${p => p.theme.white};
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  position: relative;

  &.square {
    button {
      border-radius: 0px;
      padding: 2px 20px;
      height: 40px;
      min-width: 125px;
      background: rgb(255 255 255 / 80%);
      color: ${p => p.theme.black};
    }
  }
`;

const StyledButton = styled.button<{
  bgColor: string;
  textColor: string;
  borderColor?: string;
  borderRadius: string;
  hoverBgColor?: string;
  hoverBorderColor?: string;
  hoverTextColor?: string;
  height?: string;
  fontWeight?: string;
}>`
  width: auto;
  box-shadow: none !important;
  outline: none !important;
  font-size: 12px;
  padding: 6px 15px 6px 10px;
  height: ${props => props.height || '45px'};
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  border: 1px solid ${props => props.borderColor || 'unset'};
  border-radius: ${props => props.borderRadius};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 0.95)};
  transition: background-color 0.3s, border-color 0.3s, color 0.3s;
  font-weight: ${props => props.fontWeight};
  transition: fill 0.3s;
  font-family: 'Open Sans';
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props =>
      (!props.disabled && props.hoverBgColor) || props.bgColor};
    color: ${props =>
      (!props.disabled && props.hoverTextColor) || props.textColor};
    border-color: ${props =>
      (!props.disabled && props.hoverBorderColor) || props.borderColor};
  }
`;

const IconWrapper = styled.div<{ hoverSvgColor?: string; isHovered: boolean }>`
  padding: 0 10px 0 0px;
  svg {
    transition: fill 0.3s;
  }
  img {
    width: 22px;
    height: 22px;
  }

  svg path {
    fill: ${props =>
      props.isHovered && props.hoverSvgColor
        ? props.hoverSvgColor
        : 'currentColor'};
  }
`;

const SvgLabelWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

export default IconButton;
